import { motion, useScroll, useSpring } from "framer-motion";
import { useActiveSection } from "../../hooks/useActiveSection";
import "./ProgressRail.css";
import { goToSection } from "../../lib/sectionFlow";

const SECTIONS = [
    { id: "home", label: "Index" },
    { id: "about", label: "Profile" },
    { id: "experience", label: "Experience" },
    { id: "projects", label: "Work" },
    { id: "contact", label: "Contact" },
];

const IDS = SECTIONS.map((s) => s.id);

/**
 * A fixed rail in the left margin: section numbers, and a hairline that fills
 * with overall scroll progress.
 *
 * It earns its place by doing a job — telling you where you are in a long
 * single page, and getting you elsewhere in one click. That is the distinction
 * worth holding: an effect that measures something real reads as engineering,
 * where the same pixels spent on drifting shapes read as filler.
 *
 * Numbers only, no text labels. The page content is 1200px wide, so on a
 * 1440px screen the margin is 120px per side — an expanding label would have
 * run straight under the content. The name of each section lives in the
 * button's accessible label instead, where it costs no width.
 */
export default function ProgressRail() {
    const active = useActiveSection(IDS, 80);
    const { scrollYProgress } = useScroll();

    // Spring the raw progress so the fill glides rather than tracking the
    // scroll wheel's own stepping.
    const fill = useSpring(scrollYProgress, {
        stiffness: 90,
        damping: 28,
        mass: 0.4,
    });

    const go = (id: string) => {
        const el = document.getElementById(id);
        if (!el) return;
        if (!goToSection(id)) el.scrollIntoView({ behavior: "smooth", block: "start" });
        window.history.replaceState(null, "", `#${id}`);
    };

    return (
        <aside className="rail" aria-label="Section progress">
            <div className="rail-track" aria-hidden="true">
                <motion.div
                    className="rail-fill"
                    style={{ scaleY: fill, transformOrigin: "top" }}
                />
            </div>

            <ol className="rail-list">
                {SECTIONS.map((section, i) => {
                    const isActive = active === section.id;
                    return (
                        <li key={section.id}>
                            <button
                                type="button"
                                className={`rail-item mono ${isActive ? "is-active" : ""}`}
                                onClick={() => go(section.id)}
                                aria-label={`Go to ${section.label}`}
                                aria-current={isActive ? "true" : undefined}
                            >
                                {String(i + 1).padStart(2, "0")}
                            </button>
                        </li>
                    );
                })}
            </ol>
        </aside>
    );
}
